import axiosClient from 'api/axiosClient';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown, { KTBSDropdownDirections } from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDateRangePicker, {
    dateFilter,
    getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AgencySaleOrderListFilterView from '../AgencySaleOrderListView/components/AgencySaleOrderListFilterView';
import AgencyPremiseOrderListFilterView from './components/AgencyPremiseOrderListFilterView';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import AgencySaleDialogView from '../AgencySaleDialogView';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';

AgencyPremiseOrderListView.propTypes = {
    showFullUI: PropTypes.bool,
    prefKey: PropTypes.string,
    agencyId: PropTypes.string,
    premiseOrderListFilter: PropTypes.object,
};

function AgencyPremiseOrderListView({ prefKey, showFullUI = true, premiseOrderListFilter, agencyId }) {
    // MARK: ---- PROPERTIES ----
    const router = useRouter();
    const operatorList = useSelector((state) => state?.app?.appConfig?.operators) ?? [];
    const { user, agencyRoleList } = useSelector((state) => state.auth);
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';

    const defaultColumnIds = useMemo(() => {
        return [
            'SALE_ID',
            'AGENCY_ID',
            'ORDER_TYPE',
            'AMOUNT',
            'STATUS',
            'CONTRACT_STATUS',
            'CONTRACT_NUMBER',
            'CONTRACT',
            'USER_ID',
            'TIME',
        ];
    }, []);
    const [filter, setFilter] = useState();
    useEffect(() => {
        if (!isAdmin) {
            setFilter({ ...AgencyHelper.Filter.premiseOrderList, buyerId: user?.agency?.agencyId });
        } else {
            setFilter({
                page: 0,
                limit: 10,
                q: '',
                status: '',
                orderType: '',
                contractStatus: '',
                buyerId: agencyId,
                dateFilter: dateFilter.baMuoiNgayGanNhat,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
            });
        }
    }, [prefKey, user, isAdmin]);
    const [agencyPremiseList, setAgencyPremiseList] = useState([]);
    const [total, setTotal] = useState(0);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'SALE_ID',
                name: UIHelper.buildTableColumn('Mã đơn hàng'),
                selector: (row) => row?.saleCode ?? row?.saleId,
                width: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'AGENCY_ID',
                name: UIHelper.buildTableColumn('Người bán hàng/giới thiệu'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            AgencyHelper.Navigation.navigateToAgencyDetail(null, row?.agency);
                        }}
                    >
                        {row?.agency?.name}
                    </span>
                ),
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {
                id: 'ORDER_TYPE',
                name: UIHelper.buildTableColumn('Loại đơn hàng'),
                selector: (row) => AgencyHelper.Utils.getSaleOrderTypeName(row?.orderType),
                width: '180px',
                style: tableStyle.columns.normal,
            },
            // {
            //     id: 'AMOUNT',
            //     name: UIHelper.buildTableColumn('Tổng tiền', 'VNĐ'),
            //     selector: (row) => Utils.formatNumber(row?.amount ?? 0),
            //     right: true,
            //     width: '140px',
            //     style: tableStyle.columns.normal,
            // },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái đơn hàng'),
                selector: (row) => (
                    <KTLabel
                        text={AgencyHelper.Utils.getEffectiveSaleOrderStatusName(row)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${AgencyHelper.Utils.getEffectiveSaleOrderStatusColor(row)}`}
                    />
                ),
                minWidth: '200px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'CONTRACT_STATUS',
                name: UIHelper.buildTableColumn('Trạng thái hợp đồng'),
                selector: (row) => {
                    return !!_.first(row?.contracts) ? (
                        <KTLabel
                            text={AgencyHelper.Utils.getContractStatusName(_.first(row?.contracts)?.status)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${AgencyHelper.Utils.getContractStatusColor(
                                _.first(row?.contracts)?.status
                            )}`}
                            additionalDescription={row?.cancelingReason}
                        />
                    ) : (
                        <></>
                    );
                },
                minWidth: '160px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'CONTRACT_NUMBER',
                name: UIHelper.buildTableColumn('Số hợp đồng'),
                selector: (row) => row?.contractNumber,
                width: '160px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'CONTRACT',
                name: UIHelper.buildTableColumn('Hợp đồng'),
                selector: (row) => {
                    return row?.contracts?.length > 0 ? (
                        <span
                            className="cursor-pointer hover-opacity-60"
                            onClick={() => {
                                console.log({ user, row });
                                if (isAdmin || user?.agency?.agencyId === row?.buyerId) {
                                    Utils.openInNewTab(Utils.getFullUrl(_.first(row?.contracts)?.contractDocument));
                                } else {
                                    ToastHelper.showError('Bạn không có quyền xem hợp đồng này');
                                }
                            }}
                        >
                            Xem chi tiết
                        </span>
                    ) : (
                        <></>
                    );
                },
                minWidth: '140px',
                style: tableStyle.columns.link,
            },
            {
                id: 'USER_ID',
                name: UIHelper.buildTableColumn('Người tạo đơn'),
                selector: (row) => (
                    <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
                        {row?.user?.fullname}
                    </span>
                ),
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {
                id: 'TIME',
                name: UIHelper.buildTableColumn('Ngày tạo'),
                selector: (row) => Utils.formatDateTimeToString(row?.time, 'DD/MM/YYYY - HH:mm'),
                width: '160px',
                style: tableStyle.columns.normal,
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                maxWidth: '140px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                center: false,
                cell: (row) => {
                    return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
                },
            },
        ];
    }, [operatorList, user]);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
        saveChanges,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
    useEffect(() => {
        if (Utils.isValid(filter)) {
            let params = { ...filter };
            params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');
            if (isAdmin) {
                requestGetAgencySaleList(params);
            } else {
                AgencyHelper.API.requestGetMySaleList(params).then((res) => {
                    if (res) {
                        setAgencyPremiseList(res.orders);
                        setTotal(res.total);
                    }
                });
            }
        }
    }, [filter, isAdmin]);
    const windowSize = useWindowSize();

    // MARK: ---- METHODS ----
    const requestGetAgencySaleList = async (params) => {
        const filteredParams = _.pickBy(params, (value) => value !== '');
        try {
            const res = await axiosClient.get('/sale', { params: filteredParams });
            const { result, sales, total } = res;
            if (result === 'success') {
                setAgencyPremiseList(sales);
                setTotal(total);
            }
        } catch (error) { }
    };
    const handleRefresh = (newFilter) => {
        if (Utils.isValid(prefKey)) {
            AgencyHelper.Filter.premiseOrderList = {
                ...AgencyHelper.Filter.premiseOrderList,
                ...newFilter,
            };
            setFilter(AgencyHelper.Filter.premiseOrderList);
        } else {
            setFilter(newFilter);
        }
    };

    const buildMoreActions = (row) => {
        const contract = _.first(row?.contracts);
        const actions = [];
        // xem chi tiet
        actions.push({
            text: 'Xem chi tiết',
            iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
            click: () => {
                router.navigate(`${row?.saleId}`)
            },
        });
        if (isAdmin) {
            actions.push({
                text: 'Chỉnh sửa',
                iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
                click: () => {
                    Utils.openInNewTab(`${AppRoute.orderList}/add/${row?.saleId}`);
                },
            });

            actions.push({
                text: 'Hủy đơn hàng',
                iconElement: (
                    <i
                        className="fa-regular fa-times-circle"
                        style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                    />
                ),
                click: () => {
                    AppCustomDialogHelper.show(
                        '',
                        <AgencySaleDialogView
                            iconEl={<img src={AppResource.images.dialogIcons.dialogError} />}
                            title="Hủy đơn hàng"
                            contentEl={
                                <>
                                    Bạn có chắc muốn hủy đơn hàng:{' '}
                                    <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                                        {row?.saleId}
                                    </span>
                                    ?
                                </>
                            }
                            onCancel={() => {
                                AppCustomDialogHelper.hide();
                            }}
                            onConfirm={async () => {
                                // const uncancelledContract = row?.contracts?.find(contract => contract?.status !== AgencyConstants.ContractStatus.CANCELLED);
                                // if (!!uncancelledContract) {
                                //     if (uncancelledContract?.status === AgencyConstants.ContractStatus.EXPIRED) {
                                //         ToastHelper.showError(`Không thể hủy đơn hàng vì tồn tại hợp đồng ${uncancelledContract?.contractNumber} đã hết hạn`)
                                //     } else {
                                //         ToastHelper.showError(`Không thể hủy đơn hàng vì tồn tại hợp đồng ${uncancelledContract?.contractNumber} đang chạy`)
                                //     }
                                // } else {
                                AppCustomDialogHelper.hide();
                                let updateProductSuccess = true;
                                let updateContractSuccess = true;
                                if (row?.orderType === AgencyConstants.SaleOrderType.LANDLORD) {
                                    updateProductSuccess = await AgencyHelper.API.requestUpdatePremise(
                                        row?.premise?.premiseId,
                                        Utils.transformEmptyStrings({
                                            agencyId: null,
                                        })
                                    );
                                    if (!updateProductSuccess) {
                                        ToastHelper.showSuccess(`Cập nhật thông tin mặt bằng không thành công`);
                                    }
                                }
                                const uncancelledContract = row?.contracts?.find(contract => contract?.status !== AgencyConstants.ContractStatus.CANCELLED);
                                if (!!uncancelledContract) {
                                    updateContractSuccess = await AgencyHelper.API.requestUpdateContract(uncancelledContract?.contractId, {
                                        status: AgencyConstants.ContractStatus.CANCELLED,
                                        cancelingReason: "Hủy đơn hàng",
                                    });
                                    if (!updateContractSuccess) {
                                        ToastHelper.showError(`Cập nhật trạng thái hợp đồng không thành công`);
                                    }
                                }
                                if (updateProductSuccess && updateContractSuccess) {
                                    const result = await AgencyHelper.API.requestUpdateSale(row?.saleId, {
                                        status: AgencyConstants.SaleOrderStatus.CANCELLED,
                                    });
                                    if (result && _.isBoolean(result)) {
                                        ToastHelper.showSuccess('Hủy đơn hàng thành công');
                                        handleRefresh({ ...filter });
                                    } else {
                                        ToastHelper.showError('Hủy đơn hàng thất bại');
                                    }
                                }
                                // }
                            }}
                        />,
                        'md',
                        false,
                        true
                    );
                },
            });
            if (
                (row?.status === AgencyConstants.SaleOrderStatus.ORDERED &&
                    (!Utils.isValid(contract) ||
                        [AgencyConstants.ContractStatus.CREATED, AgencyConstants.ContractStatus.CANCELLED].includes(
                            contract?.status
                        )))
                || row?.status === AgencyConstants.SaleOrderStatus.CANCELLED
            )
                actions.push({
                    text: 'Xoá đơn hàng',
                    iconElement: (
                        <i
                            className="fa-regular fa-trash-can"
                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                        />
                    ),
                    click: () => {
                        AppCustomDialogHelper.show(
                            '',
                            <AgencySaleDialogView
                                iconEl={<img src={AppResource.images.dialogIcons.dialogDeleteVehicle} />}
                                title="Xoá đơn hàng"
                                contentEl={
                                    <>
                                        Bạn có chắc muốn xoá đơn hàng:{' '}
                                        <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                                            {row?.saleId}
                                        </span>
                                        ?
                                    </>
                                }
                                onCancel={() => {
                                    AppCustomDialogHelper.hide();
                                }}
                                onConfirm={async () => {
                                    AppCustomDialogHelper.hide();
                                    const result = await AgencyHelper.API.requestDeleteSale(row?.saleId);
                                    if (result && _.isBoolean(result)) {
                                        ToastHelper.showSuccess('Xoá đơn hàng thành công');
                                        handleRefresh({ ...filter });
                                    } else {
                                        ToastHelper.showError(result || 'Xoá đơn hàng thất bại');
                                    }
                                }}
                            />,
                            'md',
                            false,
                            true
                        );
                    },
                });
        }
        return actions;
    };

    return (
        <div className="">
            {UIHelper.buildCard(
                <div className="pb-4">
                    {/* header */}
                    {showFullUI ? (
                        <div className="px-4 pt-4 pb-4 d-flex justify-content-between align-items-center">
                            {/* title */}
                            <div className="d-flex flex-column align-items-start">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Đơn cho thuê MB & mua hàng
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng:{' '}
                                    <span style={{ color: AppColors.primary }}>
                                        {Utils.formatNumber(total)} đơn hàng
                                    </span>
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-4"></div>
                    )}

                    {/* table */}
                    <div>
                        {/* filter */}
                        <div className="row m-0 p-0 px-2">
                            {/* tim kiem */}
                            <div className="col mb-4 px-2">
                                {UIHelper.buildFormLabel('Tìm kiếm')}
                                <KTFormSearch
                                    name="search"
                                    placeholder="Theo mã đơn hàng, người bán hàng/giới thiệu, người tạo đơn..."
                                    value={AgencyHelper.Filter.premiseOrderList.q}
                                    onSubmit={(text) => {
                                        handleRefresh({
                                            ...filter,
                                            page: 0,
                                            q: text,
                                        });
                                    }}
                                />
                            </div>

                            {/* loc theo trang thai don hang */}
                            <div className="col mb-4 px-2">
                                {UIHelper.buildFormLabel('Trạng thái đơn hàng')}
                                <KTBSDropdown
                                    name="status"
                                    toggleElement={
                                        <button
                                            className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                            type="button"
                                        >
                                            {_.find(AgencyHelper.Data.orderStatusOptions, { value: filter?.status })
                                                ?.name ?? 'Tất cả'}
                                        </button>
                                    }
                                    selectedValue={filter?.status}
                                    dropdownMenuItems={[
                                        { name: 'Tất cả', value: '' },
                                        ...AgencyHelper.Data.orderStatusOptions,
                                    ]}
                                    dropdownMenuClassName="overflow-auto w-100"
                                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                                    onChange={(value) => {
                                        handleRefresh({
                                            ...filter,
                                            page: 0,
                                            status: value,
                                        });
                                    }}
                                    isDropdownForm={true}
                                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                                />
                            </div>

                            {/* loc theo trang thai hop dong */}
                            <div className="col mb-4 px-2">
                                {UIHelper.buildFormLabel('Trạng thái hợp đồng')}
                                <KTBSDropdown
                                    name="contractStatus"
                                    toggleElement={
                                        <button
                                            className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                            type="button"
                                        >
                                            {_.find(AgencyHelper.Data.contractOptions, { value: filter?.contractStatus })
                                                ?.name ?? 'Tất cả'}
                                        </button>
                                    }
                                    selectedValue={filter?.contractStatus}
                                    dropdownMenuItems={[
                                        { name: 'Tất cả', value: '' },
                                        ...AgencyHelper.Data.contractOptions,
                                    ]}
                                    dropdownMenuClassName="overflow-auto w-100"
                                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                                    onChange={(value) => {
                                        handleRefresh({
                                            ...filter,
                                            page: 0,
                                            contractStatus: value,
                                        });
                                    }}
                                    isDropdownForm={true}
                                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                                />
                            </div>

                            {/* thiet lap cot */}
                            <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                                {/* bo loc */}
                                <ButtonFilterDropdown
                                    dropdownContent={
                                        <AgencyPremiseOrderListFilterView
                                            initialFilter={filter}
                                            onCancel={(newFilter) => {
                                                handleRefresh(newFilter);
                                            }}
                                            onApply={(newFilter) => {
                                                handleRefresh(newFilter);
                                            }}
                                        />
                                    }
                                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                                />
                                <DropdownTableConfigColumn
                                    columnIds={columnIds}
                                    fullColumns={fullColumns}
                                    fullColumnIds={fullColumnsIdSorted}
                                    onChangeCheckboxValue={onChangeCheckboxValue}
                                    resetDefaultTableColumn={resetDefaultTableColumn}
                                    onChangeFullColumnIds={(ids) => {
                                        setFullColumnIds(ids);
                                        const key = prefKey + '_FULL_COLUMN_IDS';
                                        localStorage.setItem(key, ids.join('|'));
                                    }}
                                    onSelectAll={onSelectAll}
                                    onUnSelectAll={onUnSelectAll}
                                    saveChanges={saveChanges}
                                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                                />
                            </div>
                        </div>

                        {/* table */}
                        <div className="px-4">
                            <KTTable
                                columns={columns}
                                items={agencyPremiseList}
                                selectableRows={false}
                                striped={false}
                                compactGrid={false}
                                onClickedRow={(row) => { }}
                                onSelectedRowsChanged={(rows) => { }}
                                onDoubleClickedRow={(row) => { }}
                                noDataComponent={UIHelper.buildEmptyNoData(
                                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                                )}
                            />
                        </div>

                        {/* phan trang */}
                        <div className="px-4 mt-2">
                            <KTPagination
                                currentPage={filter?.page + 1}
                                rowCount={total}
                                rowsPerPage={filter?.limit}
                                currentItemInPageCount={agencyPremiseList?.length}
                                onChangePage={(newPage) => {
                                    const iNewPage = parseInt(newPage);
                                    handleRefresh({
                                        ...filter,
                                        page: iNewPage - 1,
                                    });
                                }}
                                onChangeRowsPerPage={(newRowsPerPage) => {
                                    const iNewRowsPerPage = parseInt(newRowsPerPage);
                                    handleRefresh({
                                        ...filter,
                                        limit: iNewRowsPerPage,
                                        page: 0,
                                    });
                                }}
                                loading={false}
                                color={KTPaginationColor.success}
                                appendText="đơn hàng"
                            />
                        </div>
                    </div>
                </div>,
                false,
                !showFullUI
            )}
        </div>
    );
}

export default AgencyPremiseOrderListView;
